/**
 * Visual Config — Default values and breakdown rules.
 *
 * All chart defaults live here for easy discovery and tuning.
 */
import type {
  ChartTypeOption,
  TypeStyle,
  ChartStyleState,
  ChartFieldsConfig,
  ChartEditorState,
  BreakdownRule,
  SortOption,
} from './types';

// ── Per-chart-type default type-specific styles ────────────────────────────

export const DEFAULT_TYPE_STYLES: Record<ChartTypeOption, TypeStyle> = {
  kpi: {
    showSparkline: true,
    sparklinePosition: 'right',
    lineStyle: 'smooth',
    lineWidth: 2,
    areaOpacity: 0.2,
    showMarkers: false,
    markerSize: 3,
  },
  line: {
    showMarkers: true,
    markerSize: 3,
    lineStyle: 'regular',
    lineWidth: 2,
  },
  area: {
    showMarkers: false,
    markerSize: 3,
    lineStyle: 'regular',
    lineWidth: 2,
    areaOpacity: 0.25,
  },
  bar: {
    borderRadius: 0,
    stacking: 'off' as const,
    horizontal: false,
  },
  histogram: {
    borderRadius: 0,
    stacking: 'off' as const,
    horizontal: false,
  },
  table: {},
  pie: {
    donut: true,
    innerRadius: 40,
    outerRadius: 70,
    sortSlices: false,
    labelsOutside: true,
  },
  funnel: {
    sortDescending: true,
    showConversion: true,
    gapSize: 1,
  },
  scatter: {
    showMarkers: true,
    markerSize: 8,
    markerShape: 'circle',
    regressionLine: false,
  },
  heatmap: {
    heatmapColorIndex: 0,
    showSlider: false,
    cellRadius: 0,
  },
};

// ── Default chart style (common visual presentation) ───────────────────────

export const DEFAULT_CHART_STYLE: ChartStyleState = {
  palette: 'default',
  seriesColors: {},
  showLabels: false,
  showPercent: false,
  abbreviateNumbers: true,
  showTotal: false,
  labelPosition: 'outside',
  showXAxisLabel: false,
  showYAxisLabel: true,
  xAxisTitle: '',
  yAxisTitle: '',
  startAtZero: true,
  reverseAxis: false,
  scaleType: 'linear',
  showLegend: 'auto',
  legendPosition: 'top',
  sharedTooltip: true,
  crosshair: false,
  showGridLines: false,
  breakdownSort: 'none',
  typeStyle: { ...DEFAULT_TYPE_STYLES.bar },
};

// ── Default chart fields config (sort/limit) ───────────────────────────────

export const DEFAULT_CHART_FIELDS_CONFIG: ChartFieldsConfig = {
  sort: 'none',
  limit: null,
  limitOthers: false,
};

// ── Global editor state defaults ───────────────────────────────────────────

export const DEFAULT_EDITOR_STATE: ChartEditorState = {
  chartType: 'bar',
  chartStyle: { ...DEFAULT_CHART_STYLE },
  chartFieldsConfig: { ...DEFAULT_CHART_FIELDS_CONFIG },
};

// ── Breakdown rules per chart type ─────────────────────────────────────────

export const BREAKDOWN_RULES: Record<ChartTypeOption, BreakdownRule> = {
  kpi:         { enabled: false, allowed: [] },
  line:        { enabled: true,  allowed: ['dimension', 'time'], maxCardinality: 20 },
  area:        { enabled: true,  allowed: ['dimension', 'time'], maxCardinality: 20 },
  bar:         { enabled: true,  allowed: ['dimension', 'time'], maxCardinality: 20 },
  scatter:     { enabled: true,  allowed: ['dimension'],         maxCardinality: 12 },
  histogram:   { enabled: true,  allowed: ['dimension'],         maxCardinality: 8 },
  pie:         { enabled: false, allowed: [] },
  table:       { enabled: false, allowed: [] },
  funnel:      { enabled: false, allowed: [] },
  heatmap:     { enabled: false, allowed: [] },
};

// ── Per-chart-type sort & limit defaults ───────────────────────────────────

export interface SortLimitDefaults {
  sort: SortOption;
  limit: number | null;
  /** Default value for "Others" grouping when limit is applied */
  limitOthers: boolean;
  /** Hide sort/limit controls entirely for this chart type */
  hidden: boolean;
}

export const DEFAULT_SORT_LIMIT: Record<ChartTypeOption, SortLimitDefaults> = {
  kpi:       { sort: 'none',       limit: null, limitOthers: false, hidden: true  },
  bar:       { sort: 'value_desc', limit: 10,   limitOthers: false, hidden: false },
  pie:       { sort: 'value_desc', limit: 5,    limitOthers: true,  hidden: false },
  line:      { sort: 'none',       limit: null, limitOthers: false, hidden: false },
  area:      { sort: 'none',       limit: null, limitOthers: false, hidden: false },
  table:     { sort: 'none',       limit: 100,  limitOthers: false, hidden: false },
  heatmap:   { sort: 'none',       limit: null, limitOthers: false, hidden: false },
  scatter:   { sort: 'none',       limit: null, limitOthers: false, hidden: true  },
  histogram: { sort: 'none',       limit: null, limitOthers: false, hidden: true  },
  funnel:    { sort: 'none',       limit: null, limitOthers: false, hidden: true  },
};

/**
 * Whether sort/limit UI should be shown for a given chart type + x-axis semantic.
 *
 * Time-based X axes (line/area over time) DO show the sort UI now: the
 * default is chronological-ascending (handled server-side by
 * ``apply_chart_fields_defaults``) but users can still pick value-based
 * or descending sort via the same dropdown.  ``xSemanticType`` is kept
 * in the signature for future per-type rules.
 */
export function shouldShowSortLimit(
  chartType: ChartTypeOption,
  _xSemanticType?: string,
): boolean {
  if (DEFAULT_SORT_LIMIT[chartType].hidden) return false;
  return true;
}

// ── Per-chart-type chart style overrides ────────────────────────────────────
// Merged on top of DEFAULT_CHART_STYLE when a chart is created or the type
// is switched.  Adding percentage labels for a new chart type = one entry here.

export const CHART_STYLE_OVERRIDES: Partial<Record<ChartTypeOption, Partial<ChartStyleState>>> = {
  pie:    { showPercent: true },
  funnel: { showPercent: true },
};

/**
 * Get the default type-specific style for a given chart type.
 */
export function getTypeStyleDefaults(chartType: ChartTypeOption): TypeStyle {
  return { ...DEFAULT_TYPE_STYLES[chartType] };
}

/**
 * Get the full default ChartStyleState for a given chart type,
 * including per-type overrides (e.g. showPercent for pie/funnel).
 */
export function getChartStyleDefaults(chartType: ChartTypeOption): ChartStyleState {
  return {
    ...DEFAULT_CHART_STYLE,
    typeStyle: getTypeStyleDefaults(chartType),
    ...CHART_STYLE_OVERRIDES[chartType],
  };
}
